import { useTranslation } from "react-i18next";
import Modal from "../../../../../../components/base/Modal";
import type { Driver } from "../../../../../../store/admin/driversStore";
import type { ThemeClasses } from "../../../../../../types/components";
import useIsMonitor from "../../../../../../hooks/useIsMonitor";
import { useDisableCRUD } from "../../../../../../store/admin/companiesStore";
import { CRUDTooltip } from "../../../../../../components/base/CRUDTooltip";
interface DriverDetailsModalProps {
  isDarkMode: boolean;
  themeClasses: ThemeClasses;
  driver: Driver;
  getStatusColor: (status: string) => string;
  getStatusText: (status: string) => string;
  formatDate: (
    timestamp:
      | { _seconds: number; _nanoseconds: number }
      | string
      | undefined
      | null,
  ) => string;
  onClose: () => void;
  onEdit: () => void;
}

export default function DriverDetailsModal({
  isDarkMode,
  themeClasses,
  driver,
  getStatusColor,
  getStatusText,
  formatDate,
  onClose,
  onEdit,
}: DriverDetailsModalProps) {
  const { t } = useTranslation("adminSections");
  const { isMonitor, isReadOnly } = useIsMonitor();
  const disableCRUD = useDisableCRUD();
  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase();
  };

  const hasProfileImg = driver.profileImg && driver.profileImg.trim() !== "";

  return (
    <Modal
      title={t("drivers.modal.detailsTitle")}
      themeClasses={themeClasses}
      onClose={onClose}
      width="max-w-3xl"
      buttonAction={
        !isMonitor &&
        !isReadOnly && (
          <CRUDTooltip>
            <button
              onClick={onEdit}
              disabled={disableCRUD}
              className="bg-primary-500 hover:bg-primary-400 text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("drivers.actions.edit")}
            </button>
          </CRUDTooltip>
        )
      }
      content={
        <div className="space-y-6 w-[680px] max-w-full">
          {/* Header Profile */}
          <div className="flex flex-col items-center justify-center">
            {hasProfileImg ? (
              <img
                src={driver.profileImg}
                alt={`${driver.firstName} ${driver.lastName}`}
                className={`w-32 h-32 rounded-full object-cover border-4 ${
                  isDarkMode ? "border-dark-border" : "border-gray-200"
                } mb-4`}
              />
            ) : (
              <div className="w-32 h-32 rounded-full flex items-center justify-center bg-primary-500 mb-4">
                <span className="text-4xl font-bold text-gray-900">
                  {getInitials(driver.firstName, driver.lastName)}
                </span>
              </div>
            )}
            <h3 className={`text-xl font-bold ${themeClasses.text}`}>
              {driver.firstName} {driver.lastName}
            </h3>
            <span
              className={`mt-2 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                (driver as any).status_id || driver.driverStatus,
              )}`}
            >
              {getStatusText((driver as any).status_id || driver.driverStatus)}
            </span>
          </div>

          {/* Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              className={`p-4 rounded-lg border ${
                themeClasses.cardBorder
              } ${isDarkMode ? "bg-dark-card" : "bg-gray-50"}`}
            >
              <span
                className={`block text-xs font-medium ${themeClasses.textSecondary} mb-1`}
              >
                {t("drivers.fields.userId")}
              </span>
              <span
                className={`block text-sm font-semibold ${themeClasses.text}`}
              >
                {driver.userId}
              </span>
            </div>
            <div
              className={`p-4 rounded-lg border ${
                themeClasses.cardBorder
              } ${isDarkMode ? "bg-dark-card" : "bg-gray-50"}`}
            >
              <span
                className={`block text-xs font-medium ${themeClasses.textSecondary} mb-1`}
              >
                {t("drivers.fields.license")}
              </span>
              <span
                className={`block text-sm font-semibold ${themeClasses.text}`}
              >
                {(driver as any).licenseId || driver.license_id}
              </span>
            </div>
            <div
              className={`p-4 rounded-lg border ${
                themeClasses.cardBorder
              } ${isDarkMode ? "bg-dark-card" : "bg-gray-50"}`}
            >
              <span
                className={`block text-xs font-medium ${themeClasses.textSecondary} mb-1`}
              >
                {t("drivers.fields.vehicle")}
              </span>
              <span
                className={`block text-sm font-semibold ${themeClasses.text}`}
              >
                {driver.vehicle_id || driver.vehicleId || t("drivers.fields.noVehicle")}
              </span>
            </div>
            <div
              className={`p-4 rounded-lg border ${
                themeClasses.cardBorder
              } ${isDarkMode ? "bg-dark-card" : "bg-gray-50"}`}
            >
              <span
                className={`block text-xs font-medium ${themeClasses.textSecondary} mb-1`}
              >
                {t("drivers.fields.licenseExp")}
              </span>
              <span
                className={`block text-sm font-semibold ${themeClasses.text}`}
              >
                {formatDate(
                  ((driver as any).licenseExp ?? driver.license_exp) as
                    | { _seconds: number; _nanoseconds: number }
                    | undefined,
                )}
              </span>
            </div>
            <div
              className={`p-4 rounded-lg border ${
                themeClasses.cardBorder
              } ${isDarkMode ? "bg-dark-card" : "bg-gray-50"}`}
            >
              <span
                className={`block text-xs font-medium ${themeClasses.textSecondary} mb-1`}
              >
                {t("drivers.fields.licenseExpedition")}
              </span>
              <span
                className={`block text-sm font-semibold ${themeClasses.text}`}
              >
                {formatDate(
                  ((driver as any).licenceExpedition ??
                    driver.license_expedition) as
                    | { _seconds: number; _nanoseconds: number }
                    | undefined,
                )}
              </span>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div
              className={`p-4 rounded-lg border ${
                themeClasses.cardBorder
              } ${isDarkMode ? "bg-dark-card" : "bg-gray-50"}`}
            >
              <span
                className={`block text-xs font-medium ${themeClasses.textSecondary} mb-1`}
              >
                {t("drivers.fields.days")}
              </span>
              <span className={`block text-2xl font-bold ${themeClasses.text}`}>
                {driver.days || 0}
              </span>
            </div>
            <div
              className={`p-4 rounded-lg border ${
                themeClasses.cardBorder
              } ${isDarkMode ? "bg-dark-card" : "bg-gray-50"}`}
            >
              <span
                className={`block text-xs font-medium ${themeClasses.textSecondary} mb-1`}
              >
                {t("drivers.fields.hours")}
              </span>
              <span className={`block text-2xl font-bold ${themeClasses.text}`}>
                {driver.hours || 0}
              </span>
            </div>
          </div>

          {/* Emergency Contacts */}
          {driver.contactsEmergency && driver.contactsEmergency.length > 0 && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h4 className={`text-sm font-semibold ${themeClasses.text} mb-3`}>
                {t("drivers.fields.emergencyContacts")}
              </h4>
              <div className="space-y-2">
                {driver.contactsEmergency.map((contact, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      isDarkMode ? "bg-[#303030]" : "bg-gray-100"
                    }`}
                  >
                    <div>
                      <span
                        className={`block text-sm font-medium ${themeClasses.text}`}
                      >
                        {contact.name}
                      </span>
                      <span className={`text-xs ${themeClasses.textSecondary}`}>
                        {contact.email}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i
                        className={`ri-phone-line ${themeClasses.textSecondary}`}
                      ></i>
                      <span className={`text-sm ${themeClasses.text}`}>
                        {contact.phone}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      }
    />
  );
}
